import {
  type CancellableRenderTask,
  getOutputScale,
  getPdfPageBaseLayouts,
  getPdfPageLayout,
  getPdfRenderWidth,
  isRenderCanceledError,
  isUnsupportedBrowserError,
  type PdfPageBaseLayout,
  type PdfPageLayout,
  PDF_RESIZE_RENDER_DEBOUNCE_MS,
  Box,
  EmptyStateBase,
  Skeleton,
  Stack,
  Text,
} from "@skailer-com/uikit";
import { initPdfJs, PDFJS_DOCUMENT_OPTIONS } from "@/components/document-viewer/pdfjs-config.ts";
import {
  EsignPageNavigation,
  type EsignPageNavigationPlacement,
} from "@/pages/esign/components/EsignPageNavigation.tsx";
import { getDocument, type PDFDocumentProxy } from "pdfjs-dist";
import { type ReactNode, type RefObject, useEffect, useEffectEvent, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

const markupMaxRenderWidth = 680;
const endReachGap = 300;
const pageScrollMarginTop = 68;

export interface EsignPagesViewerControls {
  scrollToNext: () => void;
}

export interface EsignPagesViewerProps {
  src: string | null;
  pageNavigationPlacement?: EsignPageNavigationPlacement;
  pageMarkerRoleIndexes?: Record<number, number[]>;
  renderPageOverlay?: (pageNumber: number, baseLayout: PdfPageBaseLayout) => ReactNode;
  onReachedEnd?: () => void;
  onErrorStateChange?: (hasError: boolean) => void;
  maxRenderWidth?: number;
  controlsRef?: RefObject<EsignPagesViewerControls | null>;
}

export function EsignPagesViewer({
  src,
  pageNavigationPlacement = "fixed",
  pageMarkerRoleIndexes,
  renderPageOverlay,
  onReachedEnd,
  onErrorStateChange,
  maxRenderWidth = markupMaxRenderWidth,
  controlsRef,
}: EsignPagesViewerProps) {
  const { t } = useTranslation();

  const containerRef = useRef<HTMLDivElement | null>(null);
  const pagesColumnRef = useRef<HTMLDivElement | null>(null);
  const endSentinelRef = useRef<HTMLDivElement | null>(null);
  const pdfRef = useRef<PDFDocumentProxy | null>(null);
  const pageCanvasRefs = useRef<Record<number, HTMLCanvasElement | null>>({});
  const pageContainerRefs = useRef<Record<number, HTMLDivElement | null>>({});

  const [pdfDocument, setPdfDocument] = useState<{
    pdf: PDFDocumentProxy;
    baseLayouts: Record<number, PdfPageBaseLayout>;
  } | null>(null);
  const [pageCount, setPageCount] = useState<number | null>(null);
  const [renderWidth, setRenderWidth] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isBrowserUnsupported, setIsBrowserUnsupported] = useState(false);
  const [activePage, setActivePage] = useState(1);
  const [isPageMenuOpen, setIsPageMenuOpen] = useState(false);

  useEffect(() => {
    const node =
      pageNavigationPlacement === "inside" || pageNavigationPlacement === "left"
        ? pagesColumnRef.current
        : containerRef.current;
    if (!node) return;

    let resizeTimeoutId: number | null = null;

    const updateRenderWidth = (rawWidth: number) => {
      const nextRenderWidth = Math.min(getPdfRenderWidth(rawWidth), maxRenderWidth);
      setRenderWidth((prev) => (Math.abs(prev - nextRenderWidth) < 1 ? prev : nextRenderWidth));
    };

    updateRenderWidth(node.clientWidth);

    const observer = new ResizeObserver((entries) => {
      const nextWidth = entries[0]?.contentRect.width ?? node.clientWidth;
      if (resizeTimeoutId !== null) window.clearTimeout(resizeTimeoutId);
      resizeTimeoutId = window.setTimeout(() => {
        updateRenderWidth(nextWidth);
      }, PDF_RESIZE_RENDER_DEBOUNCE_MS);
    });

    observer.observe(node);

    return () => {
      if (resizeTimeoutId !== null) window.clearTimeout(resizeTimeoutId);
      observer.disconnect();
    };
  }, [src, pageNavigationPlacement, isLoading, pageCount, maxRenderWidth]);

  useEffect(() => {
    let isActive = true;
    let loadingTask: ReturnType<typeof getDocument> | null = null;

    const loadPdf = async () => {
      setError(null);
      setIsBrowserUnsupported(false);
      setPageCount(null);
      setPdfDocument(null);
      pageCanvasRefs.current = {};
      pageContainerRefs.current = {};
      setIsPageMenuOpen(false);

      if (pdfRef.current) {
        void pdfRef.current.loadingTask.destroy();
        pdfRef.current = null;
      }

      if (!src) {
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      initPdfJs();

      try {
        loadingTask = getDocument({ url: src, ...PDFJS_DOCUMENT_OPTIONS });
        const loadedPdf = await loadingTask.promise;
        if (!isActive) {
          void loadedPdf.loadingTask.destroy();
          return;
        }

        const baseLayouts = await getPdfPageBaseLayouts(loadedPdf);
        if (!isActive) {
          void loadedPdf.loadingTask.destroy();
          return;
        }

        pdfRef.current = loadedPdf;
        setPdfDocument({ pdf: loadedPdf, baseLayouts });
        setPageCount(loadedPdf.numPages);
      } catch (loadError: unknown) {
        if (!isActive) return;
        if (isUnsupportedBrowserError(loadError)) {
          setIsBrowserUnsupported(true);
        } else {
          setError(loadError instanceof Error ? loadError.message : t("common:pdf_load_failed"));
        }
      } finally {
        if (isActive) setIsLoading(false);
      }
    };

    void loadPdf();

    return () => {
      isActive = false;
      if (loadingTask) void loadingTask.destroy();
    };
  }, [src, t]);

  useEffect(() => {
    if (!pdfDocument || !renderWidth || isLoading || pageCount === null) return;

    let isActive = true;
    const renderTasks: CancellableRenderTask[] = [];

    const applyPageLayout = (pageNumber: number, layout: PdfPageLayout) => {
      const container = pageContainerRefs.current[pageNumber];
      const canvas = pageCanvasRefs.current[pageNumber];
      if (container) {
        container.style.width = `${layout.cssWidth}px`;
        container.style.height = `${layout.cssHeight}px`;
      }
      if (canvas) {
        canvas.style.width = `${layout.cssWidth}px`;
        canvas.style.height = `${layout.cssHeight}px`;
      }
    };

    const renderPages = async () => {
      try {
        const outputScale = getOutputScale();
        const transform =
          outputScale === 1
            ? undefined
            : ([outputScale, 0, 0, outputScale, 0, 0] satisfies [
                number,
                number,
                number,
                number,
                number,
                number,
              ]);

        for (let pageNumber = 1; pageNumber <= pageCount; pageNumber += 1) {
          const baseLayout = pdfDocument.baseLayouts[pageNumber];
          if (baseLayout) applyPageLayout(pageNumber, getPdfPageLayout(baseLayout, renderWidth, 1));
        }

        for (let pageNumber = 1; pageNumber <= pageCount; pageNumber += 1) {
          if (!isActive) return;

          const canvas = pageCanvasRefs.current[pageNumber];
          const baseLayout = pdfDocument.baseLayouts[pageNumber];
          if (!canvas || !baseLayout) continue;

          const page = await pdfDocument.pdf.getPage(pageNumber);
          const scale = renderWidth / baseLayout.width;
          const viewport = page.getViewport({ scale });
          const layout = getPdfPageLayout(baseLayout, renderWidth, 1);
          applyPageLayout(pageNumber, layout);

          canvas.width = Math.max(1, Math.floor(layout.cssWidth * outputScale));
          canvas.height = Math.max(1, Math.floor(layout.cssHeight * outputScale));

          const renderTask = page.render({ canvas, transform, viewport });
          renderTasks.push(renderTask);
          await renderTask.promise;
        }
      } catch (renderError: unknown) {
        if (!isActive || isRenderCanceledError(renderError)) return;
        if (isUnsupportedBrowserError(renderError)) {
          setIsBrowserUnsupported(true);
        } else {
          setError(
            renderError instanceof Error ? renderError.message : t("common:pdf_render_failed")
          );
        }
      }
    };

    void renderPages();

    return () => {
      isActive = false;
      renderTasks.forEach((task) => {
        task.cancel();
      });
    };
  }, [pdfDocument, renderWidth, isLoading, pageCount, t]);

  useEffect(() => {
    if (pageCount === null) return;

    const ratios: Record<number, number> = {};
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const page = Number((entry.target as HTMLElement).dataset.page);
          ratios[page] = entry.intersectionRatio;
        });

        let bestPage = 1;
        let bestRatio = -1;
        Object.entries(ratios).forEach(([page, ratio]) => {
          if (ratio > bestRatio) {
            bestRatio = ratio;
            bestPage = Number(page);
          }
        });
        setActivePage(bestPage);
      },
      { threshold: [0.1, 0.25, 0.5, 0.75, 1] }
    );

    for (let pageNumber = 1; pageNumber <= pageCount; pageNumber += 1) {
      const container = pageContainerRefs.current[pageNumber];
      if (container) observer.observe(container);
    }

    return () => observer.disconnect();
  }, [pageCount]);

  useEffect(() => {
    onErrorStateChange?.(isBrowserUnsupported || error !== null);
  }, [isBrowserUnsupported, error, onErrorStateChange]);

  const handleReachedEnd = useEffectEvent(() => {
    onReachedEnd?.();
  });

  useEffect(() => {
    if (pageCount === null || isLoading) return;
    const sentinel = endSentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) handleReachedEnd();
    });
    observer.observe(sentinel);

    return () => observer.disconnect();
  }, [pageCount, isLoading]);

  useEffect(() => {
    if (!controlsRef) return;
    controlsRef.current = {
      scrollToNext: () => {
        if (pageCount === null) return;
        if (activePage < pageCount) {
          pageContainerRefs.current[activePage + 1]?.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        } else {
          endSentinelRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
        }
      },
    };
    return () => {
      controlsRef.current = null;
    };
  }, [controlsRef, activePage, pageCount]);

  const scrollToPage = (pageNumber: number) => {
    pageContainerRefs.current[pageNumber]?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const pageNavigation =
    !isLoading && pageCount !== null && pageCount > 1 && pdfDocument ? (
      <EsignPageNavigation
        placement={pageNavigationPlacement}
        pdf={pdfDocument.pdf}
        pageCount={pageCount}
        activePage={activePage}
        baseLayouts={pdfDocument.baseLayouts}
        pageMarkerRoleIndexes={pageMarkerRoleIndexes}
        isPageMenuOpen={isPageMenuOpen}
        onPageMenuOpen={() => {
          setIsPageMenuOpen(true);
        }}
        onPageClick={scrollToPage}
        onRenderError={setError}
      />
    ) : null;

  const pages = (
    <Stack direction={"vertical"} items={"center"} className={"gap-17 pt-17"}>
      {Array.from({ length: pageCount ?? 0 }, (_, index) => {
        const pageNumber = index + 1;
        return (
          <Stack key={`page-${pageNumber}`} items={"center"} className={"gap-3"}>
            <Text className={"title-sub-headline text-secondary"}>
              {String(pageNumber).padStart(2, "0")}
            </Text>
            <div
              ref={(el) => {
                pageContainerRefs.current[pageNumber] = el;
              }}
              data-page={pageNumber}
              style={{ scrollMarginTop: pageScrollMarginTop }}
              className={"relative rounded-xs bg-light shadow-sm"}
            >
              <canvas
                ref={(el) => {
                  pageCanvasRefs.current[pageNumber] = el;
                }}
                className={"rounded-md"}
              />
              {renderPageOverlay && pdfDocument?.baseLayouts[pageNumber]
                ? renderPageOverlay(pageNumber, pdfDocument.baseLayouts[pageNumber])
                : null}
            </div>
          </Stack>
        );
      })}
      <div
        ref={endSentinelRef}
        className={"pointer-events-none w-full"}
        style={{ height: endReachGap, marginTop: -endReachGap }}
        aria-hidden={true}
      />
    </Stack>
  );

  return (
    <Box ref={containerRef} className={`w-full ${isBrowserUnsupported || error ? "h-full" : ""}`}>
      {isBrowserUnsupported ? (
        <div className={"flex h-full items-center justify-center"}>
          <EmptyStateBase
            emoji={":sloth:"}
            title={t("common:pdf_unsupported_browser_title")}
            description={t("common:pdf_unsupported_browser_description")}
          />
        </div>
      ) : error ? (
        <div className={"flex h-full items-center justify-center"}>
          <EmptyStateBase
            emoji={":shrug:"}
            title={t("common:pdf_error_title")}
            description={t("common:pdf_error_description")}
          />
        </div>
      ) : isLoading || pageCount === null ? (
        <Skeleton className={"mx-auto h-215 w-full max-w-170"} />
      ) : pageNavigationPlacement === "inside" ? (
        <Stack direction={"horizontal"} className={"relative w-full gap-4"}>
          {pageNavigation}
          <div ref={pagesColumnRef} className={`min-w-0 flex-1 ${isPageMenuOpen ? "pr-6" : ""}`}>
            {pages}
          </div>
        </Stack>
      ) : pageNavigationPlacement === "left" ? (
        <Stack direction={"horizontal"} className={"w-full gap-4"}>
          {pageNavigation}
          <div ref={pagesColumnRef} className={"min-w-0 flex-1"}>
            {pages}
          </div>
        </Stack>
      ) : (
        pages
      )}

      {pageNavigationPlacement === "fixed" && pageNavigation}
    </Box>
  );
}
